/**
 * Velocity — Science.co
 *
 * Is selling speeding up or slowing down right now, and why? Today, hour by
 * hour, against the same hours of the last seven days:
 *
 *   Pace       paid, payment starts, visitors and spend over the chosen window
 *   Funnel     every step, and the first one that falls behind
 *   Channels   spend, visitors and payment starts per channel, and $ per each
 *   Landing    which pages people arrived on — a new page or a dead one shows
 *              here before it shows anywhere else
 *
 * Three sources, three freshnesses (strip at the top): Manifold charges are
 * live, the BigQuery funnel trails by 30–70 minutes, and spend lands hourly.
 * Each is compared only over the time it actually covers. The arithmetic is in
 * lib/velocity.js; the data in backend services/velocity.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'

import { velocityAPI } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Button } from '../components/ui'
import { Notice } from '../components/scienceModel/parts'
import HourlyChart from '../components/velocity/HourlyChart'
import VelocityAlerts from '../components/velocity/VelocityAlerts'
import VelocityPatterns from '../components/velocity/VelocityPatterns'
import VelocityCompare from '../components/velocity/VelocityCompare'
import {
  WINDOWS, compareWindow, compareRate, reachesFor, reachOfSeries, funnelFor, vsNormal, ratioOf, windowSpan,
} from '../lib/velocity'

const SCIENCE_CLINIC_ID = 'scienceco'
const REFRESH_MS = 5 * 60 * 1000


const int = v => (v == null ? '—' : Math.round(v).toLocaleString())
const one = v => (v == null ? '—' : v < 10 ? v.toFixed(1) : Math.round(v).toLocaleString())
const money = v => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`)
const pct = v => (v == null ? '—' : `${(v * 100).toFixed(v < 0.1 ? 1 : 0)}%`)
const signed = v => (v == null ? '' : `${v >= 0 ? '+' : ''}${Math.round(v * 100)}%`)
const clock = iso => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '—')
const hourLabel = h => {
  const whole = Math.floor(h)
  const minutes = Math.round((h - whole) * 60)
  const hh = whole % 12 === 0 ? 12 : whole % 12
  return `${hh}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}${whole < 12 || whole === 24 ? 'a' : 'p'}`
}

const STATUS_STYLE = {
  behind: 'text-destructive',
  ahead: 'text-emerald-600 dark:text-emerald-400',
  normal: 'text-muted-foreground',
  none: 'text-muted-foreground',
}
const STATUS_TEXT = { behind: 'behind normal', ahead: 'ahead of normal', normal: 'within normal', none: 'no data yet' }

/** today vs normal, coloured only when outside the noise. */
function Delta({ result }) {
  if (!result || result.status === 'none') return <span className="text-muted-foreground">—</span>
  return <span className={`font-medium ${STATUS_STYLE[result.status]}`}>{signed(vsNormal(result))}</span>
}

function PaceTile({ title, result, format = int, note, span = windowSpan(result) }) {
  return (
    <Card>
      <CardContent className="p-4 space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-xs text-muted-foreground">{title}</div>
          {/* The window this tile really covers: sources reach different times. */}
          {span && <div className="text-[11px] text-muted-foreground tabular-nums whitespace-nowrap">{span}</div>}
        </div>
        <div className="text-xl font-semibold tabular-nums">{result?.status === 'none' ? '—' : format(result?.today)}</div>
        <div className="text-xs text-muted-foreground tabular-nums">
          normal {format(result?.expected)} <span className="text-[11px]">({format(result?.low)}–{format(result?.high)})</span>
        </div>
        <div className={`text-xs font-medium ${STATUS_STYLE[result?.status || 'none']}`}>
          {signed(vsNormal(result))} {STATUS_TEXT[result?.status || 'none']}
        </div>
        {note && <div className="text-[11px] text-muted-foreground">{note}</div>}
      </CardContent>
    </Card>
  )
}

function FreshnessStrip({ data }) {
  const funnel = data.freshness.funnel || {}
  const oldestStep = Object.values(funnel).filter(Boolean).sort()[0]
  const spend = data.freshness.spend || {}
  const items = [
    { label: 'Sales (Manifold)', value: data.freshness.payments ? `live · last charge ${clock(data.freshness.payments.newest)}` : 'unavailable' },
    { label: 'Funnel (BigQuery)', value: oldestStep ? `through ${clock(oldestStep)}` : 'unavailable' },
    ...Object.keys(data.channels)
      .filter(channel => data.channels[channel].spend)
      .map(channel => {
        const hours = data.channels[channel].spend[data.today] || []
        const reach = reachOfSeries(hours)
        return { label: `${channel} spend`, value: reach.hour ? `through ${hourLabel(reach.hour)}` : `none today · pulled ${clock(spend[channel])}` }
      }),
  ]
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
      {items.map(item => (
        <span key={item.label}><span className="font-medium text-foreground">{item.label}</span> {item.value}</span>
      ))}
    </div>
  )
}


function FunnelCard({ funnel, windowLabel }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Funnel · {windowLabel}</CardTitle>
        <CardDescription className="text-xs">
          Visitors counted once a day, in the hour they first reached each step. Step rate is the share of the step above.
          {funnel.first && <> The first step outside its normal range is <span className="font-medium text-destructive">{funnel.rows.find(r => r.key === funnel.first)?.label}</span>.</>}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Step</th>
              <th className="px-3 py-2 text-right font-medium">Today</th>
              <th className="px-3 py-2 text-right font-medium">Normal</th>
              <th className="px-3 py-2 text-right font-medium">vs normal</th>
              <th className="px-3 py-2 text-right font-medium">Step rate</th>
              <th className="px-3 py-2 text-right font-medium">Normal rate</th>
              <th className="px-3 py-2 text-right font-medium">Covers</th>
            </tr>
          </thead>
          <tbody>
            {funnel.rows.map(row => (
              <tr key={row.key} className={`border-b last:border-0 ${row.key === funnel.first ? 'bg-destructive/5' : ''}`}>
                <td className="px-3 py-1.5">{row.label}</td>
                <td className="px-3 py-1.5 text-right">{row.result?.status === 'none' ? '—' : int(row.result?.today)}</td>
                <td className="px-3 py-1.5 text-right text-muted-foreground">{one(row.result?.expected)}</td>
                <td className="px-3 py-1.5 text-right"><Delta result={row.result} /></td>
                <td className="px-3 py-1.5 text-right">{pct(row.stepRate?.today)}</td>
                <td className="px-3 py-1.5 text-right text-muted-foreground">{pct(row.stepRate?.expected)}</td>
                <td className="px-3 py-1.5 text-right text-muted-foreground whitespace-nowrap">
                  {row.result && !row.result.empty ? `${hourLabel(row.result.from)}–${hourLabel(row.result.to)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

function ChannelsCard({ data, hours, funnelReach, windowLabel }) {
  const rows = useMemo(() => {
    return Object.entries(data.channels).map(([channel, grids]) => {
      const visitors = compareWindow(grids.visitors, data.days, funnelReach, hours)
      const initiated = compareWindow(grids.initiated, data.days, funnelReach, hours)
      // Spend over ITS OWN reach, and visitors re-cut to the same hours, so
      // cost per visitor never divides an hour of spend by half an hour of people.
      const spendReach = grids.spend ? reachOfSeries(grids.spend[data.today] || []) : null
      const spend = grids.spend ? compareWindow(grids.spend, data.days, spendReach, hours, { count: false }) : null
      const visitorsOnSpend = spend ? compareWindow(grids.visitors, data.days, spendReach, hours) : null
      const initiatedOnSpend = spend ? compareWindow(grids.initiated, data.days, spendReach, hours) : null
      return {
        channel, visitors, initiated, spend,
        perVisitor: spend ? ratioOf(spend, visitorsOnSpend) : null,
        perStart: spend ? ratioOf(spend, initiatedOnSpend) : null,
      }
    }).sort((a, b) => (b.visitors.today || 0) - (a.visitors.today || 0))
  }, [data, hours, funnelReach])

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Channels · {windowLabel}</CardTitle>
        <CardDescription className="text-xs">
          Channel is each visitor&apos;s first pageview of the day. Payment started counts that day&apos;s visitors who started a payment, and late in a window it is still filling in.
          Cost per visitor and per payment start use the hours spend has reported.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Channel</th>
              <th className="px-3 py-2 text-right font-medium">Spend</th>
              <th className="px-3 py-2 text-right font-medium">vs normal</th>
              <th className="px-3 py-2 text-right font-medium">Visitors</th>
              <th className="px-3 py-2 text-right font-medium">vs normal</th>
              <th className="px-3 py-2 text-right font-medium">Payment started</th>
              <th className="px-3 py-2 text-right font-medium">vs normal</th>
              <th className="px-3 py-2 text-right font-medium">$/visitor</th>
              <th className="px-3 py-2 text-right font-medium">$/payment start</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.channel} className="border-b last:border-0">
                <td className="px-3 py-1.5">{row.channel}</td>
                <td className="px-3 py-1.5 text-right">{row.spend ? money(row.spend.today) : <span className="text-muted-foreground">not ingested</span>}</td>
                <td className="px-3 py-1.5 text-right">{row.spend ? <Delta result={row.spend} /> : ''}</td>
                <td className="px-3 py-1.5 text-right">{int(row.visitors.today)}</td>
                <td className="px-3 py-1.5 text-right"><Delta result={row.visitors} /></td>
                <td className="px-3 py-1.5 text-right">{int(row.initiated.today)}</td>
                <td className="px-3 py-1.5 text-right"><Delta result={row.initiated} /></td>
                <td className="px-3 py-1.5 text-right">
                  {row.perVisitor ? <>{money(row.perVisitor.today)} <span className="text-muted-foreground">/ {money(row.perVisitor.expected)}</span></> : ''}
                </td>
                <td className="px-3 py-1.5 text-right">
                  {row.perStart ? <>{money(row.perStart.today)} <span className="text-muted-foreground">/ {money(row.perStart.expected)}</span></> : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

function LandingCard({ data, hours, funnelReach, windowLabel }) {
  const rows = useMemo(() => data.landings.map(landing => ({
    ...landing,
    visitors: compareWindow(landing.visitors, data.days, funnelReach, hours),
    initiated: compareWindow(landing.initiated, data.days, funnelReach, hours),
  })).filter(r => (r.visitors.today || 0) + r.visitors.expected >= 3)
    .sort((a, b) => Math.abs((b.visitors.today || 0) - b.visitors.expected) - Math.abs((a.visitors.today || 0) - a.visitors.expected)),
  [data, hours, funnelReach])

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Landing pages · {windowLabel}</CardTitle>
        <CardDescription className="text-xs">
          Where visitors first arrived, sorted by how far they moved from normal. A page that is new today, or one that suddenly gets traffic but no payment starts, shows up here first.
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="border-b text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">Landing page</th>
              <th className="px-3 py-2 text-left font-medium">Mostly from</th>
              <th className="px-3 py-2 text-right font-medium">Visitors</th>
              <th className="px-3 py-2 text-right font-medium">Normal</th>
              <th className="px-3 py-2 text-right font-medium">vs normal</th>
              <th className="px-3 py-2 text-right font-medium">Payment started</th>
              <th className="px-3 py-2 text-right font-medium">Normal</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.landing} className="border-b last:border-0">
                <td className="px-3 py-1.5 max-w-[22rem] truncate" title={row.landing}>{row.landing}</td>
                <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{row.channels.slice(0, 2).map(c => c.channel).join(', ')}</td>
                <td className="px-3 py-1.5 text-right">{int(row.visitors.today)}</td>
                <td className="px-3 py-1.5 text-right text-muted-foreground">{one(row.visitors.expected)}</td>
                <td className="px-3 py-1.5 text-right">
                  {row.visitors.expected < 1 && row.visitors.today >= 5
                    ? <span className="font-medium text-emerald-600 dark:text-emerald-400">new</span>
                    : <Delta result={row.visitors} />}
                </td>
                <td className="px-3 py-1.5 text-right">{int(row.initiated.today)}</td>
                <td className="px-3 py-1.5 text-right text-muted-foreground">{one(row.initiated.expected)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}

export default function VelocityPage() {
  const { currentClinicId } = useAuthStore()
  const isScience = currentClinicId === SCIENCE_CLINIC_ID

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [windowKey, setWindowKey] = useState('3h')
  // last7: the last seven days; weekday: the same weekday, last four weeks.
  const [baseline, setBaseline] = useState('last7')

  const load = useCallback(async () => {
    if (!isScience) return
    try {
      setLoading(true)
      setError(null)
      const response = await velocityAPI.get({ baseline })
      if (response.data.success) setData(response.data.data)
    } catch (err) {
      console.error('[VELOCITY] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [isScience, baseline])

  useEffect(() => {
    load()
    const timer = setInterval(load, REFRESH_MS)
    return () => clearInterval(timer)
  }, [load])

  const win = WINDOWS.find(w => w.key === windowKey)

  // How far each source reaches today. Payments are live; funnel steps each
  // reach their own table's newest event; spend reaches its last reported hour.
  const reaches = useMemo(() => reachesFor(data), [data])

  const pace = useMemo(() => {
    if (!data) return null
    const cmp = (key, opts) => (data.series[key] ? compareWindow(data.series[key], data.days, reaches[key], win.hours, opts) : null)
    return {
      paid: cmp('paid'),
      revenue: cmp('revenue', { count: false }),
      initiated: cmp('payment_initiated'),
      visitors: cmp('visitors'),
      spend: cmp('spend', { count: false }),
      acceptance: data.acceptance ? compareRate(data.acceptance[win.key]) : null,
    }
  }, [data, reaches, win])

  const funnel = useMemo(() => {
    if (!data || !data.series.visitors) return null
    const steps = [{ key: 'visitors', label: 'Visitors' }, ...data.funnelSteps]
    return funnelFor(data, steps, reaches, win.hours)
  }, [data, reaches, win])

  if (!isScience) {
    return (
      <div className="p-4 sm:p-6">
        <Notice>Velocity is built for Science.co for now. Select Science.co to view it.</Notice>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold">Velocity</h1>
          <p className="text-xs text-muted-foreground">
            Today against the same clock hours of {baseline === 'weekday' ? `the last 4 ${new Date().toLocaleDateString('en-US', { weekday: 'long', timeZone: 'America/New_York' })}s` : 'the last 7 days'} (ET). Windows start on the hour (&ldquo;This hour&rdquo; at 2:43 is 2:00–2:43), and each tile shows the span its data covers. New checkouts only. Refreshes every 5 minutes{data ? ` · as of ${clock(data.asOf)}` : ''}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground mr-1">Window</span>
            {WINDOWS.map(w => (
              <Button key={w.key} size="sm" variant={w.key === windowKey ? 'default' : 'outline'} onClick={() => setWindowKey(w.key)}>{w.label}</Button>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground mr-1">Compared with</span>
            {[['last7', 'Last 7 days'], ['weekday', 'Same weekday']].map(([k, l]) => (
              <Button key={k} size="sm" variant={baseline === k ? 'default' : 'outline'} onClick={() => setBaseline(k)}>{l}</Button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} aria-label="Refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {error && <Notice tone="error">{error}</Notice>}
      {!data && loading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      )}

      {data && (
        <>
          <VelocityAlerts />
          <VelocityCompare />
          <FreshnessStrip data={data} />
          {Object.entries(data.errors || {}).map(([source, message]) => (
            <Notice key={source} tone="error">{source} unavailable: {message}</Notice>
          ))}

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <PaceTile title="New orders" result={pace.paid} />
            <PaceTile title="Revenue" result={pace.revenue} format={money} />
            <PaceTile title="Payment started" result={pace.initiated} note="BigQuery, 30–70 min behind" />
            <PaceTile title="Visitors" result={pace.visitors} />
            <PaceTile title="Spend (Meta + Google)" result={pace.spend} format={money} note="through the last hour both reported" />
            <PaceTile
              title="Acceptance rate"
              // Acceptance counts PEOPLE over a trailing window (backend
              // acceptanceWindows), so it is the one tile that is not
              // clock-aligned, and its span says so.
              span={win.key === 'day' ? 'since midnight' : `past ${win.hours === 1 ? '60 min' : `${win.hours} h`}`}
              result={pace.acceptance}
              format={pct}
              note={pace.acceptance
                ? `${int(pace.acceptance.paid)} of ${int(pace.acceptance.attempted)} people paid a first payment · as on Declines`
                : 'unavailable'}
            />
          </div>

          <HourlyChart data={data} reaches={reaches} />
          {funnel && <FunnelCard funnel={funnel} windowLabel={win.label} />}
          {data.series.visitors && (
            <>
              <ChannelsCard data={data} hours={win.hours} funnelReach={reaches.visitors} windowLabel={win.label} />
              <LandingCard data={data} hours={win.hours} funnelReach={reaches.visitors} windowLabel={win.label} />
            </>
          )}
          <VelocityPatterns />
        </>
      )}
    </div>
  )
}
